import { Fab, Typography, useScrollTrigger } from '@material-ui/core';
import { Search, Tune } from '@material-ui/icons';
import { Color, Column, Columns } from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddLoadAlertIcon } from 'shared/icons/AddLoadAlertIcon';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import { NAVIGATION_BOTTOM_BAR_HEIGHT_CSS_VAR } from 'shared/layout/NavigationHeaderBannerHeight';
import { WebviewLayout } from 'shared/layout/WebviewLayout';
import {
  composeSavedSearchTitle,
  isEmptySearchCriteria,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { updateSearchQuery } from 'shared/utils/URLUtils';
import styled, { css } from 'styled-components';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import { usePostingsPageContext } from '../../core/postings/PostingsPageContext';
import {
  useCurrentSavedSearch,
  usePostingsPageState,
} from '../../core/postings/PostingsPageState';
import { useOpenAddSavedSearchPage } from '../../core/saved-searches/SavedSearchState';
import { useSavedSearches } from '../../data/LoadboardSavedSearchesAPI';
import {
  LoadSearchSource,
  PostingsPageTab,
} from '../../data/dto/PostingsPageParamsDTO';
import { LoadAlertDialog } from '../LoadAlertDialog';
import { PostingDetailsLoadAlertNudgeDialog } from '../posting-details/PostingDetailsLoadAlertNudge';
import { PostingDetailsPage } from '../posting-details/PostingDetailsPage';
import { PostingList } from './PostingList';
import { PostingListFilterBookableButton } from './PostingListFilterBookableButton';
import { PostingListFilterSortButton } from './PostingListFilterSortButton';
import { PostingsLoadingState } from './PostingsLoadingState';
import { PrivatePostingList } from './PrivatePostingList';
import { SavedLoadsList } from './SavedLoadsList';
import { SavedSearchList } from './SavedSearchList';
import { SuggestedLoadsSlider } from './SuggestedLoadsSlider';
import { useSuggestedLoads } from 'loadboard/data/LoadboardPostingAPI';
import { useFlag } from 'shared/settings/FeatureToggles';

const FAB_BOTTOM_OFFSET = 16;
const FAB_HEIGHT = 48;
const FAB_GAP = 16;
const FAB_CLEARANCE = FAB_BOTTOM_OFFSET + FAB_HEIGHT + FAB_GAP;
const FAB_LABEL_MAX_WIDTH = 160;

// Responsive web renders a fixed bottom navigation bar over the page; the mobile
// apps render none. Offsetting from the bar's height (0px when it is absent)
// keeps the button and its spacer the same distance above the bar as above the
// native tab bar, instead of overlapping it.
const BOTTOM_BAR_HEIGHT = `var(${NAVIGATION_BOTTOM_BAR_HEIGHT_CSS_VAR}, 0px)`;

const FloatingActionButton = styled(Fab)<{ $isCollapsed: boolean }>`
  position: fixed;
  bottom: calc(${BOTTOM_BAR_HEIGHT} + ${FAB_BOTTOM_OFFSET}px);
  right: 16px;
  color: ${Color.White};

  /* && outranks MUI's .MuiFab-extended padding; collapsed, MUI's
     48px min-width keeps it a circle around the icon */
  && {
    transition: padding 0.25s ease-in-out;

    ${({ $isCollapsed }) =>
      $isCollapsed &&
      css`
        padding: 0;
      `}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

const FloatingActionButtonLabel = styled.span<{ $isCollapsed: boolean }>`
  overflow: hidden;
  white-space: nowrap;
  max-width: ${FAB_LABEL_MAX_WIDTH}px;
  margin-left: 8px;
  opacity: 1;
  transition:
    max-width 0.25s ease-in-out,
    margin-left 0.25s ease-in-out,
    opacity 0.15s ease-in-out;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      max-width: 0;
      margin-left: 0;
      opacity: 0;
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const tabs = new Map<PostingsPageTab, string>([
  ['available', 'Available'],
  ['requested', 'Requested'],
  ['booked', 'Booked'],
  ['suggested', 'Suggested'],
  ['saved-search', 'Load Alerts'],
  ['saved-loads', 'Saved'],
  ['private-loadboard', 'Private Loadboards'],
]);

interface ContentActionsProps {
  sort: ReactNode;
  search: ReactNode;
  bookable: ReactNode;
}

function ContentActions({ search, sort, bookable }: ContentActionsProps) {
  return (
    <Box padding="xsmall">
      <Columns space="xsmall">
        <Column>{search}</Column>

        <Column width="content">{bookable}</Column>

        <Column width="content">{sort}</Column>
      </Columns>
    </Box>
  );
}

export function PostingListPage() {
  const navigate = useNavigate();
  const state = usePostingsPageState();
  const savedSearches = useSavedSearches();
  const { params, setParams } = usePostingsPageContext();
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);

  const savedSearch = useCurrentSavedSearch();
  const openAddSavedSearchPage = useOpenAddSavedSearchPage({
    savedSearches: savedSearches.data,
  });

  const isScrolled = useScrollTrigger({
    threshold: 100,
    disableHysteresis: true,
    target: rootRef.current || undefined,
  });

  const [dialogState, setDialogState] = useState<{
    source: LoadSearchSource;
  }>();

  const isSearchResults =
    (params.current_tab === 'available' ||
      params.current_tab === 'private-loadboard') &&
    !params.current_saved_search_guid &&
    !isEmptySearchCriteria(params.search_criteria);
  const shouldShowCreateLoadAlertButton =
    isSearchResults && !!state.postings?.length;

  const isSliderInAvailableEnabled = useFlag(
    'slb_suggested_slider_in_available',
  );
  const isSuggestedSliderEnabled =
    isSliderInAvailableEnabled &&
    params.current_tab === 'available' &&
    params.current_page === 0 &&
    !params.current_saved_search_guid &&
    isEmptySearchCriteria(params.search_criteria);
  const { data: suggestedLoads } = useSuggestedLoads(
    { size: 5 },
    { enabled: isSuggestedSliderEnabled },
  );
  const suggestedLoadsCount = suggestedLoads?.data?.length ?? 0;
  const showSuggestedSlider =
    isSuggestedSliderEnabled && suggestedLoadsCount > 0;

  const openFilterPage = useCallback(
    (source: LoadSearchSource, create?: boolean) => {
      navigate({
        pathname: '/filter',
        search: updateSearchQuery(location.search, (prev) => ({
          ...prev,
          utm_medium: source,
          ...(create && { load_alert: 'create' }),
        })),
      });
      if (create) {
        trackLoadboardEvent({ name: 'SLB: Clicked Create Load Alert', source });
      }
    },
    [navigate, location.search],
  );

  const handleSavedSearchCreate = useCallback(
    (source: LoadSearchSource) => {
      if (
        params.search_criteria.pickup_venues.length ||
        params.search_criteria.delivery_venues.length
      ) {
        setDialogState({ source });
        trackLoadboardEvent({ name: 'SLB: Clicked Create Load Alert', source });
      } else {
        openFilterPage(source, true);
      }
    },
    [params, openFilterPage, setDialogState],
  );

  const title = useMemo(() => {
    return savedSearch
      ? savedSearch.description ||
          composeSavedSearchTitle(savedSearch.search_criteria)
      : isSearchResults
        ? composeSavedSearchTitle(params.search_criteria)
        : tabs.get(params.current_tab);
  }, [
    isSearchResults,
    params.current_tab,
    savedSearch,
    params.search_criteria,
  ]);

  useDocumentTitle(title);

  return (
    <WebviewLayout
      contentRef={rootRef}
      contentKey={`${params.current_tab} ${params.current_page} ${params.current_page_size}`}
    >
      <PostingsLoadingState loading={state.isPostingsPageFetching} />

      {!!params.current_load_guid && <PostingDetailsPage />}

      <PostingDetailsLoadAlertNudgeDialog />

      {(params.current_tab === 'available' ||
        params.current_tab === 'private-loadboard') && (
        <ContentActions
          search={
            <Button
              fullWidth={true}
              variant="neutral"
              startIcon={
                savedSearch ? (
                  <Tune color="action" style={{ transform: 'rotate(90deg)' }} />
                ) : (
                  <Search color="action" />
                )
              }
              onClick={() => {
                openFilterPage(
                  savedSearch ? 'Edit Load Alert Button' : 'Advanced Search',
                );
              }}
            >
              {savedSearch ? 'Edit Load Alert' : 'Search & Filter'}
            </Button>
          }
          sort={
            <PostingListFilterSortButton
              value={params.page_sort}
              searchCriteria={params.search_criteria}
              onChange={(value) => {
                setParams(
                  { current_page: 0, page_sort: value },
                  { navigation: 'replace' },
                );
              }}
            />
          }
          bookable={
            <PostingListFilterBookableButton
              value={!!params.search_criteria.show_bookable}
              onChange={(value) => {
                setParams(
                  ({ search_criteria }) => ({
                    current_page: 0,
                    search_criteria: {
                      ...search_criteria,
                      show_bookable: value,
                    },
                  }),
                  { navigation: 'replace' },
                );
              }}
            />
          }
        />
      )}

      {params.current_tab === 'saved-search' ? (
        <SavedSearchList
          onAddClick={() => {
            openAddSavedSearchPage('Load Alert List');
          }}
        />
      ) : params.current_tab === 'private-loadboard' ? (
        <PrivatePostingList {...state} />
      ) : params.current_tab === 'saved-loads' ? (
        <SavedLoadsList {...state} />
      ) : (
        <>
          {showSuggestedSlider && suggestedLoads?.data && (
            <>
              <Box padding="xsmall">
                <SuggestedLoadsSlider
                  suggestedLoads={suggestedLoads.data}
                  utmMedium="Available Tab"
                />
              </Box>
              <Box padding="xsmall" paddingBottom="none">
                {/* showSuggestedSlider checks if the current tab is 'available' */}
                <TextBox variant="heading-4">Available Loads</TextBox>
              </Box>
            </>
          )}
          <PostingList {...state} />
        </>
      )}

      {shouldShowCreateLoadAlertButton && (
        <Box
          data-testid="floating-create-load-alert-spacer"
          minHeight={`calc(${BOTTOM_BAR_HEIGHT} + ${FAB_CLEARANCE}px)`}
        />
      )}

      {shouldShowCreateLoadAlertButton && (
        <FloatingActionButton
          data-testid="floating-create-load-alert"
          color="primary"
          $isCollapsed={isScrolled}
          variant="extended"
          onClick={() => {
            handleSavedSearchCreate('Search Results');
          }}
        >
          <AddLoadAlertIcon fontSize="small" />
          <FloatingActionButtonLabel $isCollapsed={isScrolled}>
            <Typography variant="h4" component="span">
              Create Load Alert
            </Typography>
          </FloatingActionButtonLabel>
        </FloatingActionButton>
      )}

      <LoadAlertDialog
        open={!!dialogState}
        source={dialogState?.source}
        onClose={() => {
          setDialogState(undefined);
        }}
        onCustomizeClick={() => {
          if (dialogState) {
            openFilterPage(dialogState.source, true);
          }
        }}
        onSuccess={({ guid, search_criteria }) => {
          setDialogState(undefined);
          void savedSearches.refetch();
          setParams(
            { search_criteria, current_saved_search_guid: guid },
            { navigation: 'replace' },
          );
        }}
      />
    </WebviewLayout>
  );
}
